/**
 * Bullseye — „Marvel: Hell's Kitchen".
 *
 * Útočí až na 5 priestorov bez ohľadu na zóny (`ATTACK_REACH` v movement.ts).
 * Veľa kariet sa pýta, či „už v tomto ťahu vyhral súboj" — `turn.wins`
 * zapisuje `finishCombat` (a Arrogant but Effective).
 */

import { shareZone } from '../map.js';
import { stepNeighbors } from '../movement.js';
import type { Ctx } from '../ops.js';
import { askChoice, drawCard, gainAction, getMap } from '../ops.js';
import type { PlayerIndex, SpaceId } from '../types.js';
import { feint } from './common.js';
import { askMove, boostSources, fighterOrNull, opponentDiscards, opponentOf } from './helpers.js';
import type { CardEffect, EffectCtx, EffectRegistry } from './types.js';

const BULLSEYE = 'bullseye';
const NEVER_MISS = 'bullseye.i-never-miss';

const bullseyeOf = (ctx: Ctx, player: PlayerIndex) =>
  ctx.state.fighters.find((f) => f.owner === player && f.defId === BULLSEYE) ?? null;

/** „If you already won a combat this turn" — súboj, ktorý práve beží, sa ešte nepočíta. */
export const wonThisTurn = (e: EffectCtx) => e.ctx.state.turn.wins?.includes(e.player) ?? false;

/** „If you started your turn in your current space, the value of this card is 5 instead." */
const iPlannedToBeHere: CardEffect = {
  during: (e, value) => {
    const me = fighterOrNull(e.ctx, e.fighterId);
    return me && e.ctx.state.turn.startSpaces[me.id] === me.spaceId ? 5 : value;
  },
};

/** „You may BOOST this attack. If you don't BOOST this attack, draw 1 card." */
const iNeverMiss: CardEffect = {
  duringChoice: (e) => {
    if (boostSources(e.ctx, e.player).length === 0) {
      drawCard(e.ctx, e.player);
      return;
    }
    askChoice(e.ctx, {
      kind: 'card',
      player: e.player,
      source: e.cardId,
      label: 'I Never Miss: boostuj útok — alebo preskoč a potiahni kartu',
      optional: true,
      from: 'hand',
      purpose: 'boost',
      side: e.role === 'defender' ? 'defender' : 'attacker',
      onSkip: { kind: 'effect', effect: NEVER_MISS, data: '' },
    });
  },
  onFollow: (ctx, choice) => void drawCard(ctx, choice.player),
};

/** „Move one of your fighters up to 1 space. Draw 1 card. Gain 1 action." */
const forMyNextTrick: CardEffect = {
  after: (e) => {
    const mine = e.ctx.state.fighters.filter((f) => f.owner === e.player).map((f) => f.id);
    askMove(e, { fighters: mine, max: 1, label: 'For My Next Trick: presuň svoju figúrku o 1' });
    drawCard(e.ctx, e.player);
    gainAction(e.ctx, e.player);
  },
};

/** „Place Bullseye in a space that shares no zones with his current space." */
const tacticalRetreat: CardEffect = {
  after: (e) => {
    const me = bullseyeOf(e.ctx, e.player);
    if (!me) return;
    const map = getMap(e.ctx);
    const occupied = new Set(e.ctx.state.fighters.map((f) => f.spaceId));
    const spaces = map.spaces.map((s) => s.id).filter((id) => !occupied.has(id) && !shareZone(map, id, me.spaceId));
    if (spaces.length === 0) return;
    askChoice(e.ctx, {
      kind: 'place',
      player: e.player,
      source: e.cardId,
      label: 'Tactical Retreat: polož Bullseya do priestoru mimo jeho zón',
      optional: false,
      fighterId: me.id,
      spaces,
    });
  },
};

/**
 * „Move Bullseye exactly 4 spaces. You may move through opposing fighters."
 * Ciele sú konce jednoduchých ciest dĺžky presne 4 (bez vracania sa na už
 * prejdený priestor); hráč vyberá cieľ, nie trasu — preto voľba `place`.
 */
const masterStrategist: CardEffect = {
  after: (e) => {
    const me = bullseyeOf(e.ctx, e.player);
    if (!me) return;
    const map = getMap(e.ctx);
    const occupied = new Set(e.ctx.state.fighters.filter((f) => f.id !== me.id).map((f) => f.spaceId));
    const ends = new Set<SpaceId>();
    const walk = (at: SpaceId, seen: Set<SpaceId>, left: number) => {
      if (left === 0) {
        if (!occupied.has(at)) ends.add(at);
        return;
      }
      for (const next of stepNeighbors(map, e.ctx.state, me, at)) {
        if (seen.has(next)) continue;
        seen.add(next);
        walk(next, seen, left - 1);
        seen.delete(next);
      }
    };
    walk(me.spaceId, new Set([me.spaceId]), 4);
    if (ends.size === 0 || e.ctx.state.turn.rooted) return;
    askChoice(e.ctx, {
      kind: 'place',
      player: e.player,
      source: e.cardId,
      label: 'Master Strategist: presuň Bullseya presne o 4 (smie cez súperov)',
      optional: false,
      fighterId: me.id,
      spaces: [...ends],
    });
  },
};

/** „You are considered to have won this combat. Move Bullseye up to 2 spaces." */
const arrogantButEffective: CardEffect = {
  after: (e) => {
    const wins = (e.ctx.state.turn.wins ??= []);
    if (!e.won) wins.push(e.player);
    const me = bullseyeOf(e.ctx, e.player);
    if (me) askMove(e, { fighters: [me.id], max: 2, label: 'Arrogant but Effective: presuň Bullseya až o 2' });
  },
};

/** „IMMEDIATELY: If you already won a combat this turn, your opponent discards 1 card." */
const rightBetweenTheEyes: CardEffect = {
  immediate: (e) => {
    if (wonThisTurn(e)) opponentDiscards(e, opponentOf(e));
  },
};

/** „If the opposing fighter was not defeated, deal 1 damage to a fighter in the opposing fighter's zone." */
const ricochet: CardEffect = {
  after: (e) => {
    const them = fighterOrNull(e.ctx, e.opponentFighterId);
    if (!them) return;
    const map = getMap(e.ctx);
    // `shareZone` pre ten istý priestor vracia false — cieľ sám je v zóne tiež.
    const targets = e.ctx.state.fighters
      .filter((f) => f.id === them.id || shareZone(map, f.spaceId, them.spaceId))
      .map((f) => f.id);
    askChoice(e.ctx, {
      kind: 'damage',
      player: e.player,
      source: e.cardId,
      label: 'Ricochet: 1 poškodenie figúrke v zóne súpera',
      optional: false,
      amount: 1,
      targets,
    });
  },
};

/** „Draw 2 cards. If you won a combat this turn, draw 1 additional card and gain 1 action." */
const studyTheTarget: CardEffect = {
  scheme: (e) => {
    const won = wonThisTurn(e);
    for (let i = 0; i < (won ? 3 : 2); i++) drawCard(e.ctx, e.player);
    if (won) gainAction(e.ctx, e.player);
  },
};

/** „If you already won a combat this turn, the value of this card is 6 instead." */
const imBetter: CardEffect = {
  during: (e, value) => (wonThisTurn(e) ? 6 : value),
};

/** „IMMEDIATELY: If you already won a combat this turn, ignore the value of your opponent's card." */
const worldsGreatestAssassin: CardEffect = {
  immediate: (e) => {
    const combat = e.ctx.state.combat;
    if (combat && wonThisTurn(e)) (combat.flags ??= []).push(e.role === 'defender' ? 'zeroAttack' : 'zeroDefense');
  },
};

export const bullseyeCards: EffectRegistry = {
  'bullseye.i-planned-to-be-here': iPlannedToBeHere,
  [NEVER_MISS]: iNeverMiss,
  'bullseye.for-my-next-trick': forMyNextTrick,
  'bullseye.tactical-retreat': tacticalRetreat,
  'bullseye.master-strategist': masterStrategist,
  'bullseye.feint': feint,
  'bullseye.arrogant-but-effective': arrogantButEffective,
  'bullseye.right-between-the-eyes': rightBetweenTheEyes,
  'bullseye.ricochet': ricochet,
  'bullseye.study-the-target': studyTheTarget,
  'bullseye.i-m-better-and-ill-prove-it': imBetter,
  'bullseye.world-s-greatest-assassin': worldsGreatestAssassin,
};

